import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { $accountForIntegrationsStore } from '@lib/store';
import { $accountTypesStore } from '@lib/store/integrations';
import type { AccountForIntegrationsRequest, AccountForIntegrationsResponse } from '@shared/api/rest/accounts';
import { AccountType, IntegrationType } from '@shared/api/rest/integrations';
import { IntegrationAccountTable } from './integrationAccountTable';

type CategorySelection = {
  [id: string]: string;
};

export interface IntegratedAccount extends AccountForIntegrationsResponse {
  editCategory?: string;
  integration: {
    category?: string;
    connected?: boolean;
  } | null;
}

const getActiveIntegration = (integration: IntegrationType, item: AccountForIntegrationsResponse) => {
  if (!item.integrations || !item.integrations.length) return null;

  const int = integration.toUpperCase();
  const result = item.integrations.filter((item) => item.serviceName?.toUpperCase() === int)[0];
  if (!result) return null;

  return {
    connected: result.connected,
    category: result.category,
  };
};

interface IAccountsProps {
  integration: IntegrationType;
  currency?: string;
  currentPage: number;
  countItemPage: number;
  onChangeCountItemPage: (count: number) => void;
  onChangePage: (index: number) => void;
}

export const Accounts = (props: IAccountsProps) => {
  const [editCategories, setEditCategories] = useState<CategorySelection>({});
  const accountStore = useUnit($accountForIntegrationsStore.store);
  const accountTypesStore = useUnit($accountTypesStore.store);
  const params: AccountForIntegrationsRequest = useMemo(
    () => ({
      Count: props.countItemPage,
      FromIndex: (props.currentPage - 1) * props.countItemPage,
      ReturnTotalCount: true,
      Currency: props.currency,
    }),
    [props.countItemPage, props.currentPage, props.currency],
  );

  useEffect(() => {
    $accountTypesStore.events.update();
  }, []);

  const items = useMemo<IntegratedAccount[] | null>(() => {
    return accountStore.data && accountStore.data.data
      ? accountStore.data.data.map((item) => ({
          ...item,
          integration: getActiveIntegration(props.integration, item),
          editCategory: editCategories[item.id],
        }))
      : null;
  }, [accountStore.data, props.integration, editCategories]);

  const clearEditCategory = useCallback(
    (accountId: number) => {
      setEditCategories((cats) => {
        const result = { ...cats };
        delete result[accountId];
        return result;
      });
    },
    [setEditCategories],
  );
  const handleCategoryChange = useCallback(
    ({ accountId, category }: { accountId: number; category: string }) => {
      setEditCategories((current) => ({
        ...current,
        [accountId]: category,
      }));
    },
    [setEditCategories],
  );
  const connectHandler = useCallback(
    (row: IntegratedAccount) => {
      const category = (row.editCategory || row.integration?.category) as AccountType;
      $accountForIntegrationsStore.events.connectAccounts({
        accountId: row.id,
        accountType: category,
      });
      clearEditCategory(row.id);
      setTimeout(() => {
        $accountForIntegrationsStore.events.getAccounts(params);
      }, 300);
    },
    [clearEditCategory, $accountForIntegrationsStore, params],
  );

  const disconnectHandler = useCallback(
    (row: IntegratedAccount) => {
      $accountForIntegrationsStore.events.disconnectAccount({
        accountId: row.id,
        service: props.integration,
      });
      setTimeout(() => {
        $accountForIntegrationsStore.events.getAccounts(params);
      }, 300);
    },
    [$accountForIntegrationsStore, params],
  );

  return (
    <>
      {accountStore.data && items && (
        <IntegrationAccountTable
          accounts={items}
          categories={accountTypesStore.data?.accountTypes}
          currentPage={props.currentPage}
          totalItemCount={accountStore.data.total}
          countItemPage={props.countItemPage}
          onChangeCategory={handleCategoryChange}
          onConnect={connectHandler}
          onDisconnect={disconnectHandler}
          onChangeCountItemPage={props.onChangeCountItemPage}
          onChangePage={props.onChangePage}
          isLoading={accountStore.loading}
        />
      )}
    </>
  );
};
